import { useApolloClient } from '@apollo/client';
import { useMemo } from 'react';
import { GEOCODE_ADDRESS, GET_AUTOCOMPLETE_PREDICTIONS } from '../services/geocoding/queries';
import type { PlacePrediction, PlacesFallback } from './usePlacePredictions';

/**
 * Our API's Places proxy as a `PlacesFallback` for `usePlacePredictions`:
 * only used when the browser cannot reach Places itself.
 */
export function useBackendPlaces(): PlacesFallback {
  const client = useApolloClient();
  return useMemo(
    () => ({
      predict: async (input: string) => {
        const { data } = await client.query({
          query: GET_AUTOCOMPLETE_PREDICTIONS,
          variables: { input },
          fetchPolicy: 'cache-first',
        });
        return (data?.getAutocompletePredictions ?? []).map((item: PlacePrediction) => ({
          placeId: item.placeId,
          description: item.description,
          mainText: item.mainText,
          secondaryText: item.secondaryText ?? '',
        }));
      },
      resolve: async (prediction: PlacePrediction) => {
        const { data } = await client.query({
          query: GEOCODE_ADDRESS,
          variables: { address: prediction.description },
          fetchPolicy: 'cache-first',
        });
        const result = data?.geocodeAddress;
        if (!result) return null;
        return {
          placeId: prediction.placeId,
          description: prediction.description,
          formattedAddress: result.formattedAddress || prediction.description,
          latitude: result.latitude,
          longitude: result.longitude,
          city: result.city ?? undefined,
          state: result.state ?? undefined,
          zipCode: result.zipCode ?? undefined,
        };
      },
    }),
    [client],
  );
}
